import { z } from 'zod';

// ============= TRANSACTION SCHEMAS =============

export const transactionSchema = z.object({
  description: z.string()
    .trim()
    .min(1, "Descrição é obrigatória")
    .max(500, "Descrição muito longa (máx 500 caracteres)")
    .refine(val => !/[<>]/.test(val), "Caracteres HTML não permitidos"),
  
  value: z.number()
    .positive("Valor deve ser positivo")
    .finite("Valor inválido")
    .max(999999999.99, "Valor muito alto"),
  
  type: z.enum(["receita", "despesa", "transferencia"]),
  
  categoryId: z.string().uuid("ID inválido"),
  accountId: z.string().uuid("ID inválido"),
  
  // Campos opcionais
  costCenterId: z.string().uuid("ID inválido").optional().or(z.literal("")),
  paymentMethodId: z.string().uuid("ID inválido").optional().or(z.literal("")),
  supplierId: z.string().uuid("ID inválido").optional().or(z.literal("")),
  
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal("")),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal("")),
  paymentDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal("")),
  
  parcels: z.number().int().min(1).max(60).nullable().optional(),
  parcelIndex: z.number().int().min(1).nullable().optional(),
  settled: z.boolean().optional(),
});

export type TransactionFormData = z.infer<typeof transactionSchema>;

// ============= ACCOUNT/CATEGORY SCHEMAS =============

export const accountSchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Nome é obrigatório")
    .max(100, "Nome muito longo (máx 100 caracteres)")
    .refine(val => !/[<>]/.test(val), "Caracteres HTML não permitidos"),
  initialBalance: z.number()
    .finite("Saldo inicial inválido")
    .optional(),
  color: z.string()
    .regex(/^#[0-9A-F]{6}$/i, "Cor inválida (formato: #RRGGBB)")
    .optional(),
});

export const categorySchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Nome é obrigatório")
    .max(200, "Nome muito longo (máx 200 caracteres)")
    .refine(val => !/[<>]/.test(val), "Caracteres HTML não permitidos"),
  description: z.string()
    .trim()
    .max(500, "Descrição muito longa (máx 500 caracteres)")
    .optional()
    .or(z.literal("")),
  type: z.enum(["receita", "despesa"]).optional(),
  color: z.string()
    .regex(/^#[0-9A-F]{6}$/i, "Cor inválida (formato: #RRGGBB)")
    .optional(),
});

// ============= AUTHENTICATION SCHEMAS =============

export const passwordSchema = z.string()
  .min(10, "Senha deve ter no mínimo 10 caracteres")
  .max(128, "Senha muito longa (máx 128 caracteres)")
  .regex(/[A-Z]/, "Senha deve conter letra maiúscula")
  .regex(/[a-z]/, "Senha deve conter letra minúscula")
  .regex(/[0-9]/, "Senha deve conter número")
  .regex(/[^A-Za-z0-9]/, "Senha deve conter caractere especial");

export const emailSchema = z.string()
  .trim()
  .email("Email inválido")
  .max(255, "Email muito longo");

export const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: z.string()
    .trim()
    .min(2, "Nome deve ter no mínimo 2 caracteres")
    .max(100, "Nome muito longo")
    .optional(),
});

export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Senha é obrigatória"),
});

// ============= HELPER FUNCTIONS =============

/**
 * Valida se uma string é um UUID válido
 */
export function isValidUUID(value: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

/**
 * Valida se uma data está no formato ISO (YYYY-MM-DD)
 */
export function isValidISODate(value: string): boolean {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoDateRegex.test(value)) return false;
  
  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Valida valores numéricos de moeda (máx 2 casas decimais)
 */
export function isValidCurrency(value: number): boolean {
  if (!Number.isFinite(value)) return false;
  if (value < 0) return false;
  const decimals = (value.toString().split('.')[1] || '').length;
  return decimals <= 2;
}
